import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type * as React from "react";
import type { RegistryEpisode, UserRole } from "../../registry";
import { EpisodeCard } from "./EpisodeCard";

interface Props {
  episode: RegistryEpisode;
  genre?: string;
  userRole?: UserRole;
}

export function SortableEpisodeCard({ episode, genre, userRole }: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: episode.pk,
    animateLayoutChanges: ({ isSorting }) => isSorting,
  });

  const { onKeyDown: dragKeyDown, ...dragListeners } = listeners ?? {};

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // The card contains a whole toolbar, and its buttons' key events bubble to
    // here. Without this guard, pressing Enter on "Validate" picked the card up
    // for reordering instead of navigating. Only keys pressed while the card
    // itself has focus start a drag.
    if (event.target !== event.currentTarget) return;
    (dragKeyDown as ((e: React.KeyboardEvent) => void) | undefined)?.(event);
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        cursor: isDragging ? "grabbing" : "grab",
      }}
      // Matches the card's own radius so the focus ring follows its corners.
      className="rounded-xl focus-ring"
      {...attributes}
      {...dragListeners}
      onKeyDown={handleKeyDown}
      // After the spreads so they win. dnd-kit already sets role="button" and
      // tabIndex; restating the role keeps it visible to readers and the
      // linter. Without a name this announced the card's whole text content.
      role="button"
      tabIndex={0}
      aria-label={`Reorder ${episode.label}`}
    >
      <EpisodeCard chapter={episode} genre={genre} userRole={userRole} />
    </div>
  );
}
